import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAppSelector } from "eduid-hooks";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { dynamicMessage } from "translation";
export function PasswordStrengthMeter({ passStateUp, password }) {
    const [zxcvbnFn, setZxcvbnFn] = useState(null);
    const minRequiredEntropy = useAppSelector((state) => state.config.password_entropy);
    const pdata = useAppSelector((state) => state.personal_data);
    const emails = useAppSelector((state) => state.emails.emails);
    const intl = useIntl();
    const pwStrengthMessages = ["pwfield.terrible", "pwfield.bad", "pwfield.weak", "pwfield.good", "pwfield.strong"];
    // Lazy load zxcvbn when component mounts
    useEffect(() => {
        let mounted = true;
        import("zxcvbn").then((module) => {
            if (mounted) {
                // Wrap in function to avoid React treating it as a state updater function
                setZxcvbnFn(() => module.default);
            }
        });
        return () => {
            mounted = false;
        };
    }, []);
    // Calculate password score directly during render
    const { pwScore, data } = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        let userInput = [];
        if ((_a = pdata.response) === null || _a === void 0 ? void 0 : _a.given_name)
            userInput.push((_b = pdata.response) === null || _b === void 0 ? void 0 : _b.given_name);
        if ((_c = pdata.response) === null || _c === void 0 ? void 0 : _c.surname)
            userInput.push((_d = pdata.response) === null || _d === void 0 ? void 0 : _d.surname);
        if ((_e = pdata.response) === null || _e === void 0 ? void 0 : _e.chosen_given_name)
            userInput.push((_f = pdata.response) === null || _f === void 0 ? void 0 : _f.chosen_given_name);
        userInput = userInput.concat(emails.map((x) => x.email));
        if (!minRequiredEntropy || !zxcvbnFn) {
            return { pwScore: 0, data: { score: 0, isTooWeak: !zxcvbnFn } };
        }
        let score = 0, minEntropy = minRequiredEntropy / 5, entropy = 0;
        const stepEntropy = minEntropy;
        const result = zxcvbnFn(password || "", userInput);
        entropy = Math.log(result.guesses);
        for (let n = 0; n < 5 && entropy > minEntropy; n++) {
            score = n;
            minEntropy += stepEntropy;
        }
        const data = { score: score, isTooWeak: entropy < minRequiredEntropy };
        return { pwScore: score, data };
    }, [pdata, emails, minRequiredEntropy, password, zxcvbnFn]);
    // Pass score up to parent component when it changes
    useEffect(() => {
        passStateUp(data);
    }, [data, passStateUp]);
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: `code form-field-error-area ${pwScore >= 3 ? "success" : ""}`, children: password !== undefined && (_jsx("div", { className: "form-group", children: dynamicMessage(intl, pwStrengthMessages[pwScore]) })) }, "1"), _jsx("div", { className: "meter-wrapper", children: _jsx("meter", { max: "4", value: pwScore, id: "password-strength-meter" }, "0") })] }));
}
